var React = require('react');
var dayjs = require('../utils/dayjsConfig');
var fieldManager = require('@managers/fieldManager');
var { reduceSampleDensity } = require('@managers/helpers');

export const WATER_INPUT_DATA = Object.freeze([
  'baseline_etc_by_kc_table',
  'adaptive_etc_by_kc_opt',
  'irrigation_precipitation',
  'wfr',
  'precipitation_h',
  'plan_irr',
]);
export const WATER_INPUT_COLORS = {
  wfr: '#0A5EF0',
  precipitation_h: '#94C7F6',
  irrigation_precipitation: '#DD776F',
  baseline_etc_by_kc_table: '#919DB4',
  adaptive_etc_by_kc_opt: '#CD91CD',
};

export var getWaterInputData = (selectedField, waterMeterDataKeys) => {
  let WaterInputDataObject = [];
  let curDateUnx = dayjs().unix();

  if (selectedField.historical_data) {
    WaterInputDataObject = loadData(
      waterMeterDataKeys,
      selectedField,
      curDateUnx
    );
  }

  let maxYAxisVal = cleanAndFormatTheData(
    WaterInputDataObject,
    selectedField,
    curDateUnx
  );

  let newWaterData = {};
  newWaterData['wfr'] = WaterInputDataObject['WFR'];
  newWaterData['precipitation_h'] = WaterInputDataObject['PRECIPITATION'];
  newWaterData['plan_irr'] = WaterInputDataObject['PAST PLANNED']
    ? WaterInputDataObject['PAST PLANNED']
    : undefined;
  newWaterData['irrigation_precipitation'] =
    WaterInputDataObject['COMBINED WATER INPUT'];
  newWaterData['baseline_etc_by_kc_table'] =
    WaterInputDataObject['BASELINE ETC'];
  newWaterData['adaptive_etc_by_kc_opt'] = WaterInputDataObject['ADAPTIVE ETC'];

  return { newWaterData, maxYAxisVal };
};

const cleanAndFormatTheData = (
  WaterInputDataObject,
  selectedField,
  curDateUnx
) => {
  let maxYAxisVal = 0;
  // 1. take all keys from all 6 lists and put them in one list of keys (dates)
  let datesKeys = [];
  Object.keys(WaterInputDataObject).forEach(waterInputType => {
    if (WaterInputDataObject[waterInputType]) {
      datesKeys = datesKeys.concat(
        Object.keys(WaterInputDataObject[waterInputType].values)
      );
    }
  });

  // add current day ts date to datesKeys array
  datesKeys.push(curDateUnx);
  datesKeys.sort();
  let copy_of_dates = [...datesKeys];

  // avoid days without values for equal spaces between ticks in x Axis
  let day_unix = 60 * 60 * 24;
  for (let i = 1; i < copy_of_dates.length; i++) {
    let days_difference = Math.floor(
      (parseInt(datesKeys[i]) - parseInt(datesKeys[i - 1])) / day_unix
    );
    if (days_difference > 0) {
      for (let j = 1; j < days_difference; j++) {
        datesKeys.push((parseInt(datesKeys[i - 1]) + day_unix * j).toString());
      }
    }
  }

  Object.keys(WaterInputDataObject).forEach(waterInputType => {
    if (!WaterInputDataObject[waterInputType]) {
      return;
    }

    // 2. fill all none existing keys with value 0 - in order to get matching keys between all lists
    fillInMissingkeys(
      datesKeys,
      waterInputType,
      WaterInputDataObject[waterInputType],
      curDateUnx
    );

    // 3. clean lists from out of season dates
    WaterInputDataObject[waterInputType] = ignoreOutOfSeasonSamples(
      WaterInputDataObject[waterInputType],
      selectedField
    );

    // 4. converts the original samples into accumulated samples
    WaterInputDataObject[waterInputType] = accumulateSamples(
      WaterInputDataObject[waterInputType],
      selectedField.geo_data.time_zone
    );

    // 5. reduces the density of the input sample data
    WaterInputDataObject[waterInputType] = reduceSampleDensity(
      WaterInputDataObject[waterInputType]
    );

    // 6. find the max y value by comparing the last samples
    if (
      WaterInputDataObject[waterInputType][
        WaterInputDataObject[waterInputType].length - 1
      ].value > maxYAxisVal
    ) {
      maxYAxisVal =
        WaterInputDataObject[waterInputType][
          WaterInputDataObject[waterInputType].length - 1
        ].value;
    }
  });

  return maxYAxisVal;
};

const loadData = (waterMeterDataKeys, selectedField, curDateUnx) => {
  let historical_data = selectedField.historical_data || {};
  let seasonStartDate = fieldManager
    .getCurrentSeasonStartDate(selectedField)
    .unix();
  let wfr,
    precipitation,
    plan_irr,
    plan_irr_past,
    irrigation_precipitation,
    baseline_etc_by_kc_table,
    adaptive_etc_by_kc_opt;

  // load wfr values
  if (
    waterMeterDataKeys.includes('wfr') ||
    waterMeterDataKeys.includes('irrigation_precipitation')
  ) {
    // Optimize: shallow copy instead of deep clone since we don't modify the original
    wfr = historical_data['wfr_length']
      ? {
          ...historical_data['wfr_length'],
          values: { ...historical_data['wfr_length'].values },
        }
      : undefined;
  }

  // load precipitation values
  if (
    waterMeterDataKeys.includes('precipitation_h') ||
    waterMeterDataKeys.includes('irrigation_precipitation')
  ) {
    // Optimize: shallow copy instead of deep clone
    precipitation = historical_data['precipitation_h']
      ? {
          ...historical_data['precipitation_h'],
          values: { ...historical_data['precipitation_h'].values },
        }
      : undefined;
  }

  // load plan-irrrigation values
  if (waterMeterDataKeys.includes('plan_irr')) {
    // Optimize: shallow copy instead of deep clone
    plan_irr = historical_data['plan_irr']
      ? {
          ...historical_data['plan_irr'],
          values: { ...historical_data['plan_irr'].values },
        }
      : undefined;
    if (plan_irr) {
      plan_irr_past = { values: {} };
      Object.keys(plan_irr && plan_irr.values).forEach(x => {
        if (seasonStartDate <= x && x <= curDateUnx) {
          plan_irr_past['values'][x] =
            plan_irr['values'][x].value || plan_irr['values'][x];
        }
      });
      plan_irr_past['values'][curDateUnx] = '0';
    }
  }

  // load etc values
  if (waterMeterDataKeys.includes('baseline_etc_by_kc_table')) {
    // Optimize: shallow copy instead of deep clone
    baseline_etc_by_kc_table = historical_data['baseline_etc_by_kc_table']
      ? {
          ...historical_data['baseline_etc_by_kc_table'],
          values: { ...historical_data['baseline_etc_by_kc_table'].values },
        }
      : undefined;
  }

  if (waterMeterDataKeys.includes('adaptive_etc_by_kc_opt')) {
    // Optimize: shallow copy instead of deep clone
    adaptive_etc_by_kc_opt = historical_data['adaptive_etc_by_kc_opt']
      ? {
          ...historical_data['adaptive_etc_by_kc_opt'],
          values: { ...historical_data['adaptive_etc_by_kc_opt'].values },
        }
      : undefined;
  }

  // load irrigation values
  if (waterMeterDataKeys.includes('irrigation_precipitation')) {
    irrigation_precipitation = {};
    let irrigation_precipitation_combined_values = {};
    if (wfr && wfr.values && precipitation && precipitation.values) {
      irrigation_precipitation_combined_values = Object.entries(
        wfr.values
      ).reduce(
        (acc, [key, value]) =>
          // if key is already in wfr, add the values, otherwise, create new pair
          ({
            ...acc,
            [key]: ((acc[key] && acc[key].value) || 0) + value.value,
          }),
        { ...precipitation.values }
      );
    } else if (wfr && wfr.values) {
      irrigation_precipitation_combined_values = wfr.values;
    } else if (precipitation && precipitation.values) {
      irrigation_precipitation_combined_values = precipitation.values;
    }
    irrigation_precipitation.values = irrigation_precipitation_combined_values;

    // In case we load wfr or precipitation_h for the combined list only
    // we remove them from their independent lists
    if (!waterMeterDataKeys.includes('wfr')) {
      wfr = undefined;
    }
    if (!waterMeterDataKeys.includes('precipitation_h')) {
      precipitation = undefined;
    }
  }

  let WaterInputDataObject = {
    WFR: wfr,
    PRECIPITATION: precipitation,
    'PAST PLANNED': plan_irr_past,
    'COMBINED WATER INPUT': irrigation_precipitation,
    'BASELINE ETC': baseline_etc_by_kc_table,
    'ADAPTIVE ETC': adaptive_etc_by_kc_opt,
  };
  return WaterInputDataObject;
};

const fillInMissingkeys = (
  datesKeys,
  waterInputType,
  waterInputData,
  curDateUnx
) => {
  for (let i = 0; i < datesKeys.length; i++) {
    if (
      datesKeys[i] <= curDateUnx &&
      !(datesKeys[i] in waterInputData.values)
    ) {
      waterInputData.values[datesKeys[i]] = 0;
    }
  }
};

const ignoreOutOfSeasonSamples = (data, selectedField) => {
  if (data) {
    let cyclicSowDate = fieldManager.getCurrentSeasonStartDate(selectedField);
    let dataValues = data.values;
    let dataValuesAsArray = Object.entries(dataValues);
    let cyclicSowDateTimeStamp = cyclicSowDate.unix();
    let filteredDataValues = dataValuesAsArray.filter(
      ([key, value]) => key >= cyclicSowDateTimeStamp
    );
    let filteredDataObject = Object.fromEntries(filteredDataValues);
    data.values = filteredDataObject;
    return data;
  }
};

const accumulateSamples = (sampels, timezone) => {
  let currentDataSet = sampels;
  let filteredItems = Object.keys(currentDataSet.values);

  // PERFORMANCE OPTIMIZATION: Calculate timezone offset once and reuse for all samples
  // Instead of calling .tz() for every sample (expensive), calculate offset once
  let timezoneOffsetMs = 0;
  if (filteredItems.length > 0 && timezone) {
    const firstTimestamp = parseInt(filteredItems[0]);
    const firstDate = dayjs.unix(firstTimestamp);
    const firstDateWithTz = firstDate.tz(timezone);
    timezoneOffsetMs = firstDateWithTz.utcOffset() * 60 * 1000; // Convert minutes to milliseconds
  }

  let waterData = filteredItems.map((sampleKey, index) => {
    let currentSample =
      currentDataSet.values[sampleKey] &&
      currentDataSet.values[sampleKey].value;
    if (typeof currentSample !== 'number') {
      currentSample = currentDataSet.values[sampleKey] || 0;
    }

    // Use pre-calculated offset instead of calling .tz() for every sample
    const timestamp = parseInt(sampleKey) * 1000; // Unix to milliseconds
    const adjustedTimestamp = timestamp + timezoneOffsetMs;

    return {
      value: parseFloat(currentSample),
      date: dayjs(adjustedTimestamp),
      index: index,
    };
  });

  // first for season start
  let res = [{ value: 0, date: waterData[0] && waterData[0].date, index: 0 }];
  if (!waterData) {
    return res;
  }
  for (let i = 0; i < waterData.length; i++) {
    res.push({
      value: waterData[i].value + res[i].value,
      date: waterData[i].date,
      index: waterData[i].index + 1,
    });
  }
  return res;
};
